import { Avatar } from '@/components/ui/avatar';
import { Status } from '@/components/ui/status';
import { Badge, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface AffiliateInfoProps {
  name: string;
  avatarUrl?: string | null;
  programName: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  // Status information for multi-program affiliates
  activePrograms?: number;
  pendingPrograms?: number;
  inactivePrograms?: number;
}

type AffiliateProgramBadgeProps = {
  programName: string;
  icon?: React.ReactNode;
  activePrograms?: number;
  pendingPrograms?: number;
  inactivePrograms?: number;
};

export function AffiliateProgramBadge({
  programName,
  icon,
  activePrograms = 0,
  pendingPrograms = 0,
  inactivePrograms = 0,
}: AffiliateProgramBadgeProps) {
  const safeActivePrograms = Math.max(0, activePrograms ?? 0);
  const safePendingPrograms = Math.max(0, pendingPrograms ?? 0);
  const safeInactivePrograms = Math.max(0, inactivePrograms ?? 0);
  const totalPrograms = safeActivePrograms + safePendingPrograms + safeInactivePrograms;

  return (
    <Badge size="xs" colorPalette="gray" maxW="full">
      <HStack gap={1} align="center" minW={0}>
        {icon}
        {totalPrograms > 0 && (
          <>
            {safeActivePrograms > 0 && <Status value="success" />}
            {safePendingPrograms > 0 && <Status value="warning" />}
            {safeInactivePrograms > 0 && <Status value="error" />}
          </>
        )}
        <Text truncate>{programName}</Text>
      </HStack>
    </Badge>
  );
}

const AffiliateInfo: React.FC<AffiliateInfoProps> = ({
  name,
  avatarUrl,
  programName,
  size = 'xs',
  activePrograms = 0,
  pendingPrograms = 0,
  inactivePrograms = 0,
}) => {
  const { t } = useTranslation();

  // Ensure all counts are valid numbers (guards against NaN or negative values)
  const safeActivePrograms = Math.max(0, activePrograms ?? 0);
  const safePendingPrograms = Math.max(0, pendingPrograms ?? 0);
  const safeInactivePrograms = Math.max(0, inactivePrograms ?? 0);

  const totalPrograms = safeActivePrograms + safePendingPrograms + safeInactivePrograms;
  const hasMultiplePrograms = totalPrograms > 1;

  // Determine status for display
  const getStatusInfo = () => {
    // For multi-program, show detailed breakdown
    if (hasMultiplePrograms) {
      const statusParts: string[] = [];
      if (safeActivePrograms > 0) {
        statusParts.push(t('affiliates.info.status.active', { count: safeActivePrograms }));
      }
      if (safePendingPrograms > 0) {
        statusParts.push(t('affiliates.info.status.pending', { count: safePendingPrograms }));
      }
      if (safeInactivePrograms > 0) {
        statusParts.push(t('affiliates.info.status.inactive', { count: safeInactivePrograms }));
      }
      return { text: statusParts.join(', '), showText: true };
    }

    // For single program, just show the dot (no text)
    return { text: null, showText: false };
  };

  const statusInfo = getStatusInfo();

  return (
    <Flex align="center" minW={0}>
      <Avatar src={avatarUrl ?? undefined} name={name} size={size} mr={2} colorPalette="primary" />
      <VStack align="flex-start" gap={0.5} minW={0}>
        <Text lineClamp={1}>{name}</Text>
        <HStack gap={1} align="center">
          <AffiliateProgramBadge
            programName={programName}
            activePrograms={safeActivePrograms}
            pendingPrograms={safePendingPrograms}
            inactivePrograms={safeInactivePrograms}
          />
          {statusInfo.showText && statusInfo.text && (
            <Text fontSize="xs" color="gray.600">
              {statusInfo.text}
            </Text>
          )}
        </HStack>
      </VStack>
    </Flex>
  );
};

export default AffiliateInfo;
